import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input,
  Select, message, Drawer, Tooltip, Popconfirm, Tabs, Upload, Avatar, Checkbox,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, EyeOutlined, UploadOutlined, PlusCircleOutlined,
  CheckCircleOutlined, CrownOutlined, EnvironmentOutlined,
} from '@ant-design/icons'
import { useMasterData, type Employee } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import ImageUploader from '@/components/ImageUploader'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import { generateCode } from '@/utils/codeGenerator'
import { api, ApiError } from '@/services/api'

const BASE = '/master/users'

const STATUS_PILL: Record<string, string> = {
  active: 'pill-green', inactive: 'pill-neutral',
}
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', inactive: 'Inactive',
}

const ROLE_PILL: Record<string, string> = {
  'Sales Executive': 'pill-info',
  'Sales Manager':   'pill-brand',
  'Accounts Executive': 'pill-green',
  'Operations Executive': 'pill-warning',
  'Admin':           'pill-danger',
  'Director':        'pill-energy',
}

const regionOptions = [
  'Andhra Pradesh', 'Gujarat', 'Karnataka', 'Maharashtra',
  'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh',
]

const SOFTWARE_ACCESS_OPTIONS = [
  { value: 'SHAKTHI_POWER', label: 'Shakthi Power — Sales, Allocations & Financial ERP' },
  { value: 'GRAND_SHAKTHI_POWER', label: 'Grand Shakthi Power — Purchase, Inventory & HR ERP' },
]

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const StatutoryDocumentUploader: React.FC<{
  value?: string | null
  onChange?: (val: string | null) => void
  labelName: string
}> = ({ value, onChange, labelName }) => {
  const [loading, setLoading] = useState(false)

  const handleFileChange = async (file: File) => {
    setLoading(true)
    try {
      try {
        const res = await api.uploadImage('users', file)
        onChange?.(res.url)
        message.success(`${labelName} uploaded successfully`)
      } catch {
        const reader = new FileReader()
        reader.onload = (e) => {
          const url = e.target?.result as string
          onChange?.(url)
          message.success(`${labelName} attached`)
        }
        reader.readAsDataURL(file)
      }
    } catch {
      message.error(`Failed to attach ${labelName}`)
    } finally {
      setLoading(false)
    }
    return false
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F8FAFC', padding: '6px 12px', borderRadius: 6, border: '1px solid #E4E8F0' }}>
          <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>✓ {labelName} Attached</span>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            href={value}
            target="_blank"
            rel="noreferrer"
            style={{ padding: 0 }}
          >
            View Document
          </Button>
          <Button
            type="link"
            danger
            size="small"
            onClick={() => onChange?.(null)}
            style={{ marginLeft: 'auto', padding: 0 }}
          >
            Remove
          </Button>
        </div>
      ) : null}
      <Upload beforeUpload={(file) => handleFileChange(file as unknown as File)} showUploadList={false}>
        <Button size="small" icon={<UploadOutlined />} loading={loading}>
          {value ? `Replace ${labelName}` : `Upload ${labelName}`}
        </Button>
      </Upload>
    </div>
  )
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const UserMaster: React.FC = () => {
  const { employees, roles, deleteEmployee, saveEmployee, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = useState<Employee | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  // ── Access control ────────────────────────────────────────────
  const currentUser  = authStore.getUser()
  const selfRecord   = employees.find(e => (e.email || '').toLowerCase() === (currentUser?.email || '').toLowerCase())
  const authRoleLC   = (currentUser?.role || '').toLowerCase()
  const empRoleLC    = (selfRecord?.role  || '').toLowerCase()
  const isFullAccess = authRoleLC === 'admin' || empRoleLC === 'admin' || empRoleLC.includes('hr')
  const visibleEmployees = isFullAccess ? employees : (selfRecord ? [selfRecord] : [])

  const activeCount    = visibleEmployees.filter(u => u.status === 'active').length
  const executiveCount = visibleEmployees.filter(u => u.role.toLowerCase().includes('executive')).length
  const regionsCount   = new Set(visibleEmployees.map(u => u.region)).size
  const [filterRole, setFilterRole] = useState<string>('')

  const filteredEmployees = isFullAccess && filterRole
    ? visibleEmployees.filter(e => e.role === filterRole)
    : visibleEmployees

  // Grand Shakthi Power's own Role Master, for the "Grand Shakthi Power
  // Role" dropdown below — fetched once; empty (not an error) if that app's
  // database isn't reachable, per api.gspRoles().
  const [gspRoles, setGspRoles] = useState<{ id: string; name: string; description?: string }[]>([])
  useEffect(() => {
    if (isFullAccess) api.gspRoles().then(setGspRoles).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullAccess])

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
    } else if (mode === 'edit' && editId) {
      const rec = employees.find(u => u.id === editId || String(u.id).toLowerCase() === String(editId).toLowerCase())
      if (rec) {
        form.setFieldsValue({
          ...rec,
          aadhaarCardUrl: rec.aadhaarCardUrl || (rec as any).aadhaarCardUpload,
          panCardUrl: rec.panCardUrl || (rec as any).panCardUpload,
        })
      } else if (masterDataLoaded && employees.length > 0) {
        goList()
      }
    }
  }, [mode, editId, employees, masterDataLoaded])

  const handleDelete = async (id: string) => {
    try {
      await deleteEmployee(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const { confirmPassword: _drop, ...rest } = values
    const isEdit = mode === 'edit'
    const existingRec = isEdit ? employees.find(e => e.id === editId) : undefined

    const aadhaarCardUrl = values.aadhaarCardUrl || values.aadhaarCardUpload || existingRec?.aadhaarCardUrl || (existingRec as any)?.aadhaarCardUpload
    const panCardUrl = values.panCardUrl || values.panCardUpload || existingRec?.panCardUrl || (existingRec as any)?.panCardUpload

    let payload: any
    if (isEdit) {
      payload = {
        ...existingRec,
        ...rest,
        aadhaarCardUrl,
        panCardUrl,
        aadhaarCardUpload: aadhaarCardUrl,
        panCardUpload: panCardUrl,
        id: editId,
      }
    } else {
      let newCode: string
      try {
        newCode = await generateCode('users')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a code — check your connection and try again.")
        return
      }
      payload = {
        ...rest,
        aadhaarCardUrl,
        panCardUrl,
        aadhaarCardUpload: aadhaarCardUrl,
        panCardUpload: panCardUrl,
        id: newCode,
        code: newCode,
      }
    }

    setSubmitting(true)
    try {
      await saveEmployee(payload, isEdit)
      message.success(isEdit ? 'Employee updated successfully' : 'Employee added successfully')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  // An employee with no Shakthi Power access never has `role` (the Shakthi
  // Power role) set — it isn't asked for on the form (see the Software
  // Access tab) — so it sits at the backend's generic 'employee' default.
  // Showing that anywhere reads as "the role I picked didn't save," when the
  // role actually picked is their Grand Shakthi Power role, a different
  // field entirely. Fall back to that for a GSP-only employee.
  const displayRole = (record: Employee): string => {
    const access = record.softwareAccess || []
    if (!access.includes('SHAKTHI_POWER') && access.includes('GRAND_SHAKTHI_POWER')) {
      return record.gspRole || 'No Grand Shakthi Power role set'
    }
    return record.role
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'code', width: 120,
      render: (v: string, record: Employee) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 12, cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); setViewRecord(record) }}
          >
            {v}
          </span>
          <Tooltip title="Add/Edit Details">
            <Button type="link" size="small" icon={<PlusCircleOutlined />} onClick={e => { e.stopPropagation(); goEdit(record.id) }} style={{ padding: 0, height: 'auto', border: 0 }} />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Name', dataIndex: 'name',
      render: (name: string, r: Employee) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar src={r.photoUrl || undefined} icon={<UserOutlined />} size={36} />
          <div>
            <div style={{ fontWeight: 600, color: '#0B1426' }}>{name}</div>
            <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Role', dataIndex: 'role',
      filters: roles.map(r => ({ text: r.name, value: r.name })),
      onFilter: (value: any, record: Employee) => record.role === value,
      render: (_: string, record: Employee) => {
        const shown = displayRole(record)
        return <span className={`pill ${ROLE_PILL[shown] || 'pill-info'}`}>{shown}</span>
      },
    },
    {
      title: 'Department', dataIndex: 'department',
    },
    {
      title: 'Phone', dataIndex: 'phone',
      render: (v: string) => (
        <span style={{ ...MONO, fontSize: 12 }}>{v}</span>
      ),
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => (
        <span className={`pill ${STATUS_PILL[s] || 'pill-neutral'}`}>{STATUS_LABEL[s] || s}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Employee) => {
        const isSelf = record.email?.toLowerCase() === currentUser?.email?.toLowerCase()
        return (
          <Space size="small" onClick={e => e.stopPropagation()}>
            <Tooltip title="View">
              <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
            </Tooltip>
            {(isFullAccess || isSelf) && (
              <Tooltip title="Edit">
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
              </Tooltip>
            )}
            {isFullAccess && !isSelf && (
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => handleDelete(record.id)}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete">
                  <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm && mode === 'new' && !isFullAccess) {
    goList()
    return null
  }

  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit'
          ? (isFullAccess ? 'Edit Employee' : 'My Details')
          : 'Add Employee'}
        subtitle="Organization employee — official, personal, statutory and bank details"
        onBack={goList}
        extra={mode === 'edit' && editId && isFullAccess ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <Form.Item name="photoUrl" label="Profile Photo" style={{ textAlign: 'center' }}>
              <ImageUploader folder="users" shape="round" />
            </Form.Item>
          </div>
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: 'Official & Employment',
                children: (
                  <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: 16 }}>
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Arun R" />
                    </Form.Item>
                    <Form.Item name="department" label="Department">
                      <Input placeholder="e.g. Sales" />
                    </Form.Item>
                    <Form.Item name="email" label="Official Email" rules={[{ required: true, type: 'email' }]}>
                      <Input type="email" placeholder="name@shakthi.com" />
                    </Form.Item>
                    <Form.Item name="phone" label="Official Phone">
                      <Input placeholder="Mobile number" style={{ ...MONO, fontSize: 12 }} />
                    </Form.Item>
                    <Form.Item name="region" label="Region" rules={[{ required: true }]}>
                      <Select showSearch placeholder="Select region" options={regionOptions.map(s => ({ value: s, label: s }))} />
                    </Form.Item>
                    <Form.Item name="dateOfJoining" label="Date of Joining">
                      <Input type="date" />
                    </Form.Item>
                    <Form.Item name="dateOfConfirmation" label="Date of Confirmation">
                      <Input type="date" />
                    </Form.Item>
                    <Form.Item name="status" label="Status" initialValue="active">
                      <Select
                        options={[
                          { value: 'active',   label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                        ]}
                      />
                    </Form.Item>
                  </div>

                  {(mode === 'new' || mode === 'edit') && (
                    <div style={{
                      background: '#F8FAFF', borderRadius: 10, padding: '14px 16px',
                      border: '1.5px dashed #C7D7F4', marginTop: 8,
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: '#2563EB', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {mode === 'new' ? 'Login Password' : 'Reset Password'}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                        <Form.Item
                          name="password"
                          label={mode === 'new' ? 'Initial Password' : 'New Password'}
                          tooltip={
                            mode === 'new'
                              ? "The employee uses this to log in for the first time. Leave blank to use the default: 'password'"
                              : "Sets a new password for this employee — existing passwords can never be viewed, only replaced. Leave blank to keep their current password unchanged."
                          }
                        >
                          <Input.Password placeholder={mode === 'new' ? "Leave blank for default 'password'" : 'Leave blank to keep current password'} />
                        </Form.Item>
                        <Form.Item
                          name="confirmPassword"
                          label="Confirm Password"
                          dependencies={['password']}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const pwd = getFieldValue('password')
                                if (!pwd || !value || pwd === value) return Promise.resolve()
                                return Promise.reject(new Error('Passwords do not match'))
                              },
                            }),
                          ]}
                        >
                          <Input.Password placeholder="Repeat the password above" />
                        </Form.Item>
                      </div>
                      <div style={{ fontSize: 12, color: '#8A93A6', marginTop: 2 }}>
                        {mode === 'new'
                          ? <>If left blank, the default password is <strong style={{ color: '#2563EB' }}>password</strong>. The employee should change it after first login.</>
                          : 'Leaving both fields blank saves every other change without touching this employee\'s password.'}
                      </div>
                    </div>
                  )}
                  </>
                ),
              },
              // Granting/revoking which applications an employee can log
              // into is an administrative action — only visible to
              // admin/HR, not on a regular employee's own "My Details" edit.
              ...(isFullAccess ? [
                {
                  key: '5',
                  label: 'Software Access',
                  children: (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 12 }}>
                        Which applications can this employee log into? Access takes effect the next time they sign in.
                      </div>
                      <Form.Item name="softwareAccess" initialValue={mode === 'new' ? ['SHAKTHI_POWER'] : []}>
                        <Checkbox.Group options={SOFTWARE_ACCESS_OPTIONS} style={{ display: 'flex', flexDirection: 'column', gap: 10 }} />
                      </Form.Item>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) => prev.softwareAccess !== next.softwareAccess}
                      >
                        {({ getFieldValue }) => {
                          const access = getFieldValue('softwareAccess') || []
                          return (
                            <>
                              {access.includes('SHAKTHI_POWER') && (
                                <Form.Item
                                  name="role"
                                  label="Shakthi Power Role"
                                  tooltip="From Shakthi Power's own Role Master — controls what this employee can view, create, edit or delete here."
                                  rules={[{ required: true, message: 'Select a Shakthi Power role' }]}
                                  style={{ maxWidth: 360, marginTop: 4 }}
                                >
                                  <Select options={roles.map(r => ({ value: r.name, label: r.name }))} />
                                </Form.Item>
                              )}
                              {access.includes('GRAND_SHAKTHI_POWER') && (
                                <Form.Item
                                  name="gspRole"
                                  label="Grand Shakthi Power Role"
                                  tooltip="From Grand Shakthi Power's own Role Master — controls what this employee can view, create, edit or delete there."
                                  rules={[{ required: true, message: 'Select a Grand Shakthi Power role — without one, this employee defaults to the lowest-privilege role and loses access there' }]}
                                  style={{ maxWidth: 360, marginTop: 4 }}
                                >
                                  <Select
                                    allowClear
                                    placeholder={gspRoles.length ? 'Select a role' : 'No roles found — check Grand Shakthi Power is reachable'}
                                    options={gspRoles.map(r => ({ value: r.name, label: r.name.split('_').join(' ').replace(/\b\w/g, c => c.toUpperCase()) }))}
                                  />
                                </Form.Item>
                              )}
                            </>
                          )
                        }}
                      </Form.Item>
                    </div>
                  ),
                },
              ] : []),
              ...(mode === 'edit' ? [
                {
                  key: '2',
                  label: 'Personal',
                  children: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: 16 }}>
                      <Form.Item name="dateOfBirth" label="Date of Birth">
                        <Input type="date" />
                      </Form.Item>
                      <Form.Item name="bloodGroup" label="Blood Group">
                        <Select options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(v => ({ value: v, label: v }))} placeholder="Select" />
                      </Form.Item>
                      <Form.Item name="personalMobile" label="Personal Mobile">
                        <Input placeholder="Personal mobile" />
                      </Form.Item>
                      <Form.Item name="altContactNumber" label="Alternative Contact">
                        <Input placeholder="Alt contact" />
                      </Form.Item>
                      <Form.Item name="personalAddress" label="Personal Address" style={{ gridColumn: 'span 2' }}>
                        <Input.TextArea rows={2} placeholder="Full address" />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: '3',
                  label: 'Statutory',
                  children: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: 16 }}>
                      <Form.Item name="aadhaarNumber" label="Aadhaar Number">
                        <Input placeholder="Aadhaar number" />
                      </Form.Item>
                      <Form.Item name="aadhaarCardUrl" label="Aadhaar Card Document">
                        <StatutoryDocumentUploader labelName="Aadhaar Card" />
                      </Form.Item>

                      <Form.Item name="panNumber" label="PAN Number">
                        <Input placeholder="PAN number" style={{ textTransform: 'uppercase' }} />
                      </Form.Item>
                      <Form.Item name="panCardUrl" label="PAN Card Document">
                        <StatutoryDocumentUploader labelName="PAN Card" />
                      </Form.Item>

                      <Form.Item name="esiNumber" label="ESI Number">
                        <Input placeholder="ESI number" />
                      </Form.Item>
                      <Form.Item name="uanNumber" label="UAN Number">
                        <Input placeholder="UAN number" />
                      </Form.Item>
                      <Form.Item name="healthInsuranceNumber" label="Health Insurance No.">
                        <Input placeholder="Health Ins number" />
                      </Form.Item>
                      <Form.Item name="healthInsuranceProvider" label="Health Ins. Provider">
                        <Input placeholder="Health Ins provider" />
                      </Form.Item>
                      <Form.Item name="termInsuranceNumber" label="Term Insurance No.">
                        <Input placeholder="Term Ins number" />
                      </Form.Item>
                      <Form.Item name="termInsuranceProvider" label="Term Ins. Provider">
                        <Input placeholder="Term Ins provider" />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: '4',
                  label: 'Bank Details',
                  children: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: 16 }}>
                      <Form.Item name="bankName" label="Bank Name">
                        <Input placeholder="Bank Name" />
                      </Form.Item>
                      <Form.Item name="branchName" label="Branch Name">
                        <Input placeholder="Branch Name" />
                      </Form.Item>
                      <Form.Item name="accountNumber" label="Account Number">
                        <Input placeholder="Account number" style={{ ...MONO, fontSize: 12 }} />
                      </Form.Item>
                      <Form.Item name="ifscCode" label="IFSC Code">
                        <Input placeholder="IFSC Code" style={{ ...MONO, textTransform: 'uppercase', fontSize: 12 }} />
                      </Form.Item>
                    </div>
                  ),
                },
              ] : []),
            ]}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Employee' : 'Save Employee'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={7} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined style={{ color: '#1677ff' }} /> Employee Master
          </h2>
          <p className="page-subtitle">
            {isFullAccess
              ? 'Organization team members — official credentials, statutory, personal, and bank records'
              : 'Your employee record on file'}
          </p>
        </div>
        {isFullAccess && (
          <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Employee</Button>
        )}
      </div>

      <KpiCardRow
        cards={[
          { label: isFullAccess ? 'Total Employees' : 'My Status', value: isFullAccess ? visibleEmployees.length : (selfRecord?.status || 'Active'), icon: <UserOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Executives', value: executiveCount, icon: <CrownOutlined style={{ fontSize: 24, color: '#7C3AED' }} />, iconBg: 'rgba(124,58,237,0.08)', borderColor: '#7C3AED' },
          { label: 'Regions Covered', value: regionsCount, icon: <EnvironmentOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
        ]}
      />

      {isFullAccess && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
          <Select
            placeholder="Filter by Role"
            allowClear
            value={filterRole || undefined}
            onChange={(v) => setFilterRole(v || '')}
            style={{ width: 220 }}
            options={roles.map(r => ({ label: r.name, value: r.name }))}
          />
        </div>
      )}

      <Card>
        <Table
          dataSource={filteredEmployees}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} employees` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar src={viewRecord?.photoUrl || undefined} icon={<UserOutlined />} size={44} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{viewRecord?.name}</div>
              <div style={{ fontSize: 12, color: '#8A93A6' }}>{viewRecord?.email}</div>
            </div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={440}
        extra={
          <Space>
            {viewRecord && (isFullAccess || viewRecord.email?.toLowerCase() === currentUser?.email?.toLowerCase()) && (
              <Tooltip title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => { goEdit(viewRecord.id); setViewRecord(null) }} />
              </Tooltip>
            )}
            {viewRecord && isFullAccess && viewRecord.email?.toLowerCase() !== currentUser?.email?.toLowerCase() && (
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        }
      >
        {viewRecord && (
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: 'Official',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <span className={`pill ${STATUS_PILL[viewRecord.status] || 'pill-neutral'}`}>
                        {STATUS_LABEL[viewRecord.status] || viewRecord.status}
                      </span>
                      <span style={{ marginLeft: 8 }} className={`pill ${ROLE_PILL[displayRole(viewRecord)] || 'pill-info'}`}>
                        {displayRole(viewRecord)}
                      </span>
                    </div>
                    <DetailRow label="Employee Code" value={<span style={MONO}>{viewRecord.code}</span>} />
                    <DetailRow label="Department" value={viewRecord.department} />
                    <DetailRow label="Official Email" value={viewRecord.email} />
                    <DetailRow label="Official Phone" value={<span style={MONO}>{viewRecord.phone}</span>} />
                    <DetailRow label="Region" value={viewRecord.region} />
                    <DetailRow label="Date of Joining" value={viewRecord.dateOfJoining} />
                    <DetailRow label="Date of Confirmation" value={viewRecord.dateOfConfirmation} />
                  </div>
                )
              },
              {
                key: '2',
                label: 'Personal',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <DetailRow label="Date of Birth" value={viewRecord.dateOfBirth} />
                    <DetailRow label="Blood Group" value={viewRecord.bloodGroup} />
                    <DetailRow label="Personal Mobile" value={viewRecord.personalMobile} />
                    <DetailRow label="Alt Contact" value={viewRecord.altContactNumber} />
                    <DetailRow label="Address" value={viewRecord.personalAddress} />
                  </div>
                )
              },
              {
                key: '3',
                label: 'Statutory',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <DetailRow label="Aadhaar Number" value={
                      <Space>
                        {viewRecord.aadhaarNumber || '—'}
                        {(viewRecord.aadhaarCardUrl || (viewRecord as any).aadhaarCardUpload) && (
                          <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            href={viewRecord.aadhaarCardUrl || (viewRecord as any).aadhaarCardUpload}
                            target="_blank"
                            rel="noreferrer"
                            style={{ padding: 0 }}
                          >
                            (View Card)
                          </Button>
                        )}
                      </Space>
                    } />
                    <DetailRow label="PAN Number" value={
                      <Space>
                        {viewRecord.panNumber || '—'}
                        {(viewRecord.panCardUrl || (viewRecord as any).panCardUpload) && (
                          <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            href={viewRecord.panCardUrl || (viewRecord as any).panCardUpload}
                            target="_blank"
                            rel="noreferrer"
                            style={{ padding: 0 }}
                          >
                            (View Card)
                          </Button>
                        )}
                      </Space>
                    } />
                    <DetailRow label="ESI Number" value={viewRecord.esiNumber} />
                    <DetailRow label="UAN Number" value={viewRecord.uanNumber} />
                    <DetailRow label="Health Ins. No." value={viewRecord.healthInsuranceNumber} />
                    <DetailRow label="Health Ins. Prov." value={viewRecord.healthInsuranceProvider} />
                    <DetailRow label="Term Ins. No." value={viewRecord.termInsuranceNumber} />
                    <DetailRow label="Term Ins. Prov." value={viewRecord.termInsuranceProvider} />
                  </div>
                )
              },
              {
                key: '4',
                label: 'Bank',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <DetailRow label="Bank Name" value={viewRecord.bankName} />
                    <DetailRow label="Branch Name" value={viewRecord.branchName} />
                    <DetailRow label="Account Number" value={<span style={MONO}>{viewRecord.accountNumber}</span>} />
                    <DetailRow label="IFSC Code" value={<span style={MONO}>{viewRecord.ifscCode}</span>} />
                  </div>
                )
              },
            ]}
          />
        )}
      </Drawer>
    </div>
  )
}

export default UserMaster
